import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { X, Shield, Flame, Plus, ChevronDown, ChevronRight } from 'lucide-react';

export default function PrinciplesSection({
  principlesAdhered = [],
  redLineConsiderations = [],
  narrative = '',
  onPrinciplesChange,
  onRedLinesChange,
  onNarrativeChange,
  mode = 'full', // 'full' or 'flag'
  flaggedItems = [] // for flag mode: array of principle/red line statements that are flagged
}) {
  const { data: immutables = [] } = useQuery({
    queryKey: ['immutables'],
    queryFn: () => base44.entities.Immutable.list(),
    initialData: []
  });

  const values = immutables.filter((i) => i.category === 'value').map((v) => v.statement);
  const principles = immutables.filter((i) => i.category === 'principle').map((p) => p.statement);
  const companyRedLines = immutables.filter((i) => i.category === 'company_red_line');
  const peopleRedLines = immutables.filter((i) => i.category === 'people_red_line');
  const allRedLines = [...companyRedLines, ...peopleRedLines];

  const allValuesPrinciples = [...values, ...principles];

  const addPrinciple = (value) => {
    if (value && !principlesAdhered.includes(value)) {
      onPrinciplesChange([...principlesAdhered, value]);
    }
  };

  const removePrinciple = (value) => {
    onPrinciplesChange(principlesAdhered.filter((p) => p !== value));
  };

  const updateRedLineConsideration = (redLineStatement, status) => {
    const updated = [...redLineConsiderations];
    const existingIndex = updated.findIndex((r) => r.red_line === redLineStatement);

    if (existingIndex >= 0) {
      updated[existingIndex] = { red_line: redLineStatement, status };
    } else {
      updated.push({ red_line: redLineStatement, status });
    }

    onRedLinesChange(updated);
  };

  const getRedLineStatus = (redLineStatement) => {
    const existing = redLineConsiderations.find((r) => r.red_line === redLineStatement);
    return existing?.status || 'not_applicable';
  };

  const toggleFlag = (item) => {
    if (flaggedItems.includes(item)) {
      onPrinciplesChange(flaggedItems.filter(i => i !== item));
    } else {
      onPrinciplesChange([...flaggedItems, item]);
    }
  };

  // Filter red lines to only show non-"not_applicable" ones in flag mode
  const applicableRedLines = mode === 'flag' 
    ? allRedLines.filter(rl => {
        const status = getRedLineStatus(rl.statement);
        return status !== 'not_applicable';
      })
    : allRedLines;

  const hasPrinciples = principlesAdhered.length > 0;
  const hasRedLines = redLineConsiderations.filter(rlc => rlc.status && rlc.status !== 'not_applicable').length > 0;
  const hasNarrative = narrative && narrative.trim().length > 0;
  const hasSummary = hasPrinciples || hasRedLines || hasNarrative;
  const [isOpen, setIsOpen] = useState(hasSummary);

  return (
    <div className="p-4 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 rounded-xl border border-indigo-500/20">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button type="button" className="flex items-center justify-between w-full text-left">
            <div className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <Shield className="w-4 h-4 text-indigo-400" />
              <Label className="text-sm font-semibold text-slate-300 cursor-pointer">Values & Principles Adherence Plan</Label>
            </div>
            {!isOpen && hasSummary && (
              <div className="flex flex-wrap gap-1 max-w-[60%] justify-end">
                {principlesAdhered.slice(0, 3).map((p, idx) => (
                  <Badge key={`p-${idx}`} variant="outline" className="text-[10px] text-indigo-400 border-indigo-500/30 max-w-[120px] truncate">{p}</Badge>
                ))}
                {principlesAdhered.length > 3 && <Badge variant="outline" className="text-[10px] text-indigo-400 border-indigo-500/30">+{principlesAdhered.length - 3}</Badge>}
                {redLineConsiderations.filter(rlc => rlc.status && rlc.status !== 'not_applicable').slice(0, 2).map((rlc, idx) => (
                  <Badge key={`rl-${idx}`} variant="outline" className={`text-[10px] max-w-[120px] truncate ${
                    rlc.status === 'considered_upheld' ? 'text-emerald-400 border-emerald-500/30' :
                    rlc.status === 'considered_navigated' ? 'text-amber-400 border-amber-500/30' :
                    'text-red-400 border-red-500/30'
                  }`}>{rlc.red_line}</Badge>
                ))}
              </div>
            )}
            {!isOpen && !hasSummary && <span className="text-xs text-slate-500 italic">None set</span>}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4 space-y-6">

      {/* Values & Principles Multi-select */}
      <div className="space-y-3">
        <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-slate-300">
          {mode === 'flag' ? 'Values & Principles (Flag if struggling)' : 'Values & Principles To Be Adhered To'}
        </Label>
        <p className="text-xs text-slate-400 mb-2">
          {mode === 'flag' ? 'Click to flag if you struggled with any principle' : 'Select the organizational values and principles you upheld'}
        </p>
        
        {mode === 'full' ? (
          <>
            <div className="flex flex-wrap gap-2 p-3 bg-slate-950/50 border border-slate-800 rounded-lg min-h-[50px]">
              {principlesAdhered.map((principle, idx) =>
              <Badge
                key={idx}
                variant="secondary"
                className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 flex items-center gap-1">

                  {principle}
                  <button
                  type="button"
                  onClick={() => removePrinciple(principle)}
                  className="ml-1 hover:text-red-400">

                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {principlesAdhered.length === 0 &&
              <span className="text-sm text-slate-500 italic">No values or principles selected</span>
              }
            </div>

            <Select value="" onValueChange={addPrinciple}>
              <SelectTrigger className="bg-slate-950 border-slate-800">
                <SelectValue placeholder="Add value or principle..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 uppercase">Values</div>
                {values.filter((v) => !principlesAdhered.includes(v)).map((value, idx) =>
                <SelectItem key={`value-${idx}`} value={value}>
                    {value}
                  </SelectItem>
                )}
                <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 uppercase border-t border-slate-800 mt-1">Principles</div>
                {principles.filter((p) => !principlesAdhered.includes(p)).map((principle, idx) =>
                <SelectItem key={`principle-${idx}`} value={principle}>
                    {principle}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </>
        ) : (
          <div className="space-y-2">
            {principlesAdhered.map((principle, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => toggleFlag(principle)}
                className={`w-full p-3 rounded-lg border transition-colors text-left ${
                  flaggedItems.includes(principle)
                    ? 'bg-amber-500/20 border-amber-500/50 text-amber-300'
                    : 'bg-slate-950/50 border-slate-800 text-slate-300 hover:bg-slate-900/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{principle}</span>
                  {flaggedItems.includes(principle) && (
                    <Badge className="bg-amber-500/30 text-amber-300 border-amber-500/50">Struggling</Badge>
                  )}
                </div>
              </button>
            ))}
            {principlesAdhered.length === 0 && (
              <span className="text-sm text-slate-500 italic">No principles selected in activity</span>
            )}
          </div>
        )}
      </div>

      {/* Red Lines Consideration */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-rose-400" />
          <Label className="text-slate-300">
            {mode === 'flag' ? 'Red Lines (Flag if struggling)' : 'Red Lines Consideration'}
          </Label>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          {mode === 'flag' ? 'Click to flag if you struggled with any red line' : 'For each red line, indicate how it was considered'}
        </p>

        {mode === 'full' ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 p-3 bg-slate-950/50 border border-slate-800 rounded-lg min-h-[50px]">
              {redLineConsiderations.filter(rlc => rlc.status && rlc.status !== 'not_applicable').map((rlc, idx) => {
                const redLine = allRedLines.find(rl => rl.statement === rlc.red_line);
                const statusColors = {
                  considered_upheld: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
                  considered_navigated: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
                  considered_violated: 'bg-red-500/20 text-red-300 border-red-500/30'
                };
                const statusLabels = {
                  considered_upheld: 'Upheld',
                  considered_navigated: 'Navigated',
                  considered_violated: 'Violated'
                };
                return (
                  <div key={idx} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-medium ${statusColors[rlc.status] || 'bg-rose-500/20 text-rose-300 border-rose-500/30'}`}>
                    <span className="max-w-[200px] truncate" title={rlc.red_line}>{rlc.red_line}</span>
                    {redLine?.category === 'company_red_line' && <span className="opacity-60">(Co)</span>}
                    {redLine?.category === 'people_red_line' && <span className="opacity-60">(Pp)</span>}
                    <Select
                      value={rlc.status}
                      onValueChange={(value) => updateRedLineConsideration(rlc.red_line, value)}
                    >
                      <SelectTrigger className="bg-transparent border-0 h-5 px-1 text-xs w-auto min-w-[80px] shadow-none focus:ring-0">
                        <SelectValue>{statusLabels[rlc.status]}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="considered_upheld">Upheld</SelectItem>
                        <SelectItem value="considered_navigated">Navigated</SelectItem>
                        <SelectItem value="considered_violated">Violated</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => updateRedLineConsideration(rlc.red_line, 'not_applicable')}
                      className="hover:text-red-400 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
              {redLineConsiderations.filter(rlc => rlc.status && rlc.status !== 'not_applicable').length === 0 &&
                <span className="text-sm text-slate-500 italic">No red lines selected — add relevant ones below</span>
              }
            </div>

            {allRedLines.filter(rl => !redLineConsiderations.some(rlc => rlc.red_line === rl.statement && rlc.status !== 'not_applicable')).length > 0 && (
              <Select
                value=""
                onValueChange={(value) => updateRedLineConsideration(value, 'considered_upheld')}
              >
                <SelectTrigger className="bg-slate-950 border-slate-800">
                  <SelectValue placeholder="+ Add red line consideration..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 uppercase">Company Red Lines</div>
                  {companyRedLines.filter(rl => !redLineConsiderations.some(rlc => rlc.red_line === rl.statement && rlc.status !== 'not_applicable')).map((rl, idx) =>
                    <SelectItem key={`crl-${idx}`} value={rl.statement}>{rl.statement}</SelectItem>
                  )}
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 uppercase border-t border-slate-800 mt-1">People Red Lines</div>
                  {peopleRedLines.filter(rl => !redLineConsiderations.some(rlc => rlc.red_line === rl.statement && rlc.status !== 'not_applicable')).map((rl, idx) =>
                    <SelectItem key={`prl-${idx}`} value={rl.statement}>{rl.statement}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {applicableRedLines.map((redLine, idx) => {
              const status = getRedLineStatus(redLine.statement);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => toggleFlag(redLine.statement)}
                  className={`w-full p-3 rounded-lg border transition-colors text-left ${
                    flaggedItems.includes(redLine.statement)
                      ? 'bg-red-500/20 border-red-500/50 text-red-300'
                      : 'bg-slate-950/50 border-slate-800 text-slate-300 hover:bg-slate-900/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <span className="text-sm font-medium">{redLine.statement}</span>
                      <div className="flex items-center gap-2 mt-1">
                        {redLine.category === 'company_red_line' &&
                        <Badge variant="outline" className="text-xs border-rose-500/30 text-rose-400">Company</Badge>
                        }
                        {redLine.category === 'people_red_line' &&
                        <Badge variant="outline" className="text-xs border-rose-500/30 text-rose-400">People</Badge>
                        }
                        <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                          {status === 'considered_upheld' ? 'Upheld' :
                           status === 'considered_navigated' ? 'Navigated' :
                           status === 'considered_violated' ? 'Violated' : status}
                        </Badge>
                      </div>
                    </div>
                    {flaggedItems.includes(redLine.statement) && (
                      <Badge className="bg-red-500/30 text-red-300 border-red-500/50">Struggling</Badge>
                    )}
                  </div>
                </button>
              );
            })}
            {applicableRedLines.length === 0 && (
              <span className="text-sm text-slate-500 italic">No applicable red lines in activity</span>
            )}
          </div>
        )}
      </div>

      {/* Narrative */}
      <div className="space-y-2">
        <Label className="text-slate-300">Narrative on Principles & Red Lines</Label>
        <p className="text-xs text-slate-400 mb-2">Briefly describe how principles were applied or red lines were navigated</p>
        <Textarea data-testid="act-principlessection-textarea"
          value={narrative}
          onChange={(e) => onNarrativeChange(e.target.value)}
          className="bg-slate-950 border-slate-800 min-h-[100px]"
          placeholder="Describe how you applied organizational values and principles, and how you considered red lines..." />

      </div>
        </CollapsibleContent>
      </Collapsible>
    </div>);

}